'use client'

import { confirm } from '@repo/ui/Confirm'
import { useEffect, useState } from 'react'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import MUIButton from '@mui/material/Button'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

const HTML_OPTIONS = [
  {
    value: 'HtmlPElementWithEmailConversationId',
    label: 'Email Conversation ID',
    description: 'Recommended for best email syncing reliability'
  },
  {
    value: 'HtmlPElementWithClientNoAndMatterNo',
    label: 'Client and Matter number',
    description:
      'A more user-friendly option, though in some cases Osprey may not be able to apply all Matter History syncing settings'
  },
  {
    value: 'HtmlHyperlinkElementWithEmailConversationId',
    label: 'Client and Matter number hyperlink',
    description:
      'Similar to the option above, but includes the full Email Conversation Id hidden in the link, for better syncing performance'
  },
  {
    value: 'HtmlPElementWhiteTextWithEmailConversationId',
    label: 'Email Conversation ID displayed in white text',
    description:
      'Invisible to the user, but may be stripped out by email providers such as Gmail or deleted accidentally'
  },
  {
    value: 'NoIdentification',
    label: 'No identification',
    description: 'Email syncing will not perform as expected unless a value is added to the subject below'
  }
]

const PLAIN_TEXT_OPTIONS = [
  {
    value: 'EmailConversationId',
    label: 'Email Conversation ID',
    description: 'Recommended for best email syncing reliability'
  },
  {
    value: 'ClientNoMatterNo',
    label: 'Client and Matter number',
    description:
      'A more user-friendly option, though in some cases Osprey may not be able to apply all Matter History syncing settings'
  },
  {
    value: 'NoIdentification',
    label: 'No identification',
    description: 'Email syncing will not perform as expected unless a value is added to the subject below'
  }
]

const SUBJECT_OPTIONS = [
  {
    value: 'EmailConversationId',
    label: 'Append Email Conversation ID',
    description: 'Recommended for best email syncing reliability'
  },
  {
    value: 'ClientNoMatterNo',
    label: 'Append Client and Matter number',
    description:
      'A more user-friendly option, though in some cases Osprey may not be able to apply all Matter History syncing settings'
  },
  {
    value: 'NoIdentification',
    label: 'No identification',
    description:
      'Email syncing will not perform as expected unless a value is added to the HTML or Plain Text Email Body options above.'
  }
]

export const EmailTrackingSettings = () => {
  const { fetchEmailIdentification, setEmailIdentification } = useLookupsStore()
  const [htmlEmailIdentification, setHtmlEmailIdentification] = useState('')
  const [plainTextEmailIdentification, setPlainTextEmailIdentification] = useState('')
  const [subjectEmailIdentification, setSubjectEmailIdentification] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await fetchEmailIdentification()
        setHtmlEmailIdentification(result?.htmlEmailIdentification ?? '')
        setPlainTextEmailIdentification(result?.plainTextEmailIdentification ?? '')
        setSubjectEmailIdentification(result?.subjectEmailIdentification ?? '')
      } catch (error) {
        console.error('Error fetching email identification settings:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleRadioClick = (stateKey: string, value: string) => {
    const setter = {
      htmlEmailIdentification: setHtmlEmailIdentification,
      plainTextEmailIdentification: setPlainTextEmailIdentification,
      subjectEmailIdentification: setSubjectEmailIdentification
    }[stateKey]

    setter!(value)
  }

  const allNoIdentification =
    htmlEmailIdentification === 'NoIdentification' &&
    plainTextEmailIdentification === 'NoIdentification' &&
    subjectEmailIdentification === 'NoIdentification'

  const handleSave = async () => {
    if (allNoIdentification) {
      const proceed = await confirm(
        'Are you sure you want to save this configuration with No Identification for all areas? This will mean email syncing to Matter History does not work as expected.'
      )
      if (!proceed) return
    }

    setIsSaving(true)
    try {
      await setEmailIdentification({
        htmlEmailIdentification,
        plainTextEmailIdentification,
        subjectEmailIdentification
      })
    } catch (error) {
      console.error('Error saving email identification settings:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <MUIStack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <MUICircularProgress />
      </MUIStack>
    )
  }

  const renderRadioGroup = (
    label: string,
    stateKey: string,
    value: string,
    options: { value: string; label: string; description: string }[]
  ) => (
    <MUIStack direction="column" gap={1} sx={{ my: '16px' }}>
      <MUITypography variant="h5" sx={{ textTransform: 'uppercase', mt: 2, mb: 1 }}>
        {label}
      </MUITypography>
      <MUIRadioGroup value={value}>
        {options.map((option) => (
          <MUIFormControlLabel
            key={option.value}
            value={option.value}
            control={<MUIRadio onClick={() => handleRadioClick(stateKey, option.value)} />}
            label={
              <MUIStack sx={{ py: 2 }}>
                <MUITypography variant="body1" sx={{ fontWeight: '600' }}>
                  {option.label}
                </MUITypography>
                <MUITypography variant="body2" color="text.secondary">
                  {option.description}
                </MUITypography>
              </MUIStack>
            }
          />
        ))}
      </MUIRadioGroup>
    </MUIStack>
  )

  return (
    <MUIStack data-testid="EmailTrackingSettings" direction="column" gap={4}>
      <MUITypography variant="body2" sx={{ mt: -1, mb: 2 }}>
        <em>
          Configure how emails are identified and tracked for email syncing. These settings determine what
          identification is added to outbound emails to enable accurate syncing with Matter History.
        </em>
      </MUITypography>

      {renderRadioGroup(
        'HTML Email Body tracking options',
        'htmlEmailIdentification',
        htmlEmailIdentification,
        HTML_OPTIONS
      )}
      {renderRadioGroup(
        'Plain Text Email Body tracking options',
        'plainTextEmailIdentification',
        plainTextEmailIdentification,
        PLAIN_TEXT_OPTIONS
      )}
      {renderRadioGroup(
        'Email Subject tracking options',
        'subjectEmailIdentification',
        subjectEmailIdentification,
        SUBJECT_OPTIONS
      )}

      <MUIButton
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSave}
        disabled={isSaving}
        sx={{ maxWidth: 160, width: '100%', margin: '0 0 0 auto' }}
      >
        {isSaving ? <MUICircularProgress size={24} color="inherit" /> : 'Save'}
      </MUIButton>
    </MUIStack>
  )
}
